import React, {
  useCallback, useMemo, useState,
} from 'react';
import {useFormContext} from 'react-hook-form';
import fetch from 'cross-fetch';
import csc from 'country-state-city';
import styled from 'styled-components';

// Automatically initializes the iframe-resizer contentWindow handler
import 'iframe-resizer';

import {GridFieldset} from 'components/Fieldset';
import {GridLabel} from 'components/Label';
import GridField from 'components/GridField';
import Textbox from 'components/Textbox';
import Select from 'components/Select';
import Radio from 'components/Radio';
import StepForm from 'components/StepForm';
import Textarea from 'components/Textarea';

const Step1 = () => {
  const {register, formState, errors} = useFormContext();
  const {dirtyFields} = formState;

  return (
    <GridFieldset
      title="Let’s start with some basic details."
      subtitle="Step 1 of 3"
      description={(
        <>
          Tell us about you and your business. This information will be used to build your terms
          and conditions.
        </>
      )}
      columns="1fr"
      layout={[
        'name',
        'businessName',
        'email',
        'websiteUrl',
      ]}
    >
      <GridLabel title="What is your name?" area="name">
        <Textbox name="name" placeholder="e.g., Jean-luc Picard" ref={register({required: true})} />
      </GridLabel>
      <GridLabel title="What is your business’ full legal name?" area="businessName">
        <Textbox name="businessName" placeholder="e.g. Starfleet LLC" ref={register({required: true})} />
      </GridLabel>
      <GridLabel
        title="What is your email address?"
        area="email"
        description="Use an email address you're okay with sharing publicly (i.e. on your privacy policy page)."
      >
        <Textbox
          name="email"
          placeholder="e.g. picard@starfleet.org"
          ref={register({
            required: true,
            pattern: /^.+?@.+?[.].{2,}?$/,
          })}
          $invalid={dirtyFields.email && errors.email}
        />
      </GridLabel>
      <GridLabel title="What is your website URL?" area="websiteUrl">
        <Textbox
          name="websiteUrl"
          placeholder="e.g. https://starfleet.org"
          ref={register({
            required: true,
            pattern: /^(http[s]?:\/\/)?.+?[.].{2,}?$/,
          })}
          $invalid={dirtyFields.websiteUrl && errors.websiteUrl}
        />
      </GridLabel>
    </GridFieldset>
  );
};

Step1.fields = [
  'name',
  'businessName',
  'email',
  'websiteUrl',
];

const Step2 = () => {
  const {register, watch} = useFormContext();
  const country = watch('country');
  const countries = useMemo(() => csc.getAllCountries().map(({isoCode, name}) => ({
    value: isoCode,
    label: name,
  })), []);
  const states = useMemo(() => (
    country
      ? csc.getStatesOfCountry(country).map(({isoCode, name}) => ({
        value: isoCode,
        label: name,
      }))
      : []
  ), [country]);

  return (
    <GridFieldset
      title="Where is your business headquartered?"
      subtitle="Step 2 of 3"
      description={(
        <>
          Your terms and conditions should include your business' location.
        </>
      )}
      columns="1fr 1fr"
      layout={[
        'country state',
        'city postalCode',
        'address address',
      ]}
      mobileColumns="1fr"
      mobileLayout={[
        'country',
        'state',
        'city',
        'postalCode',
        'address',
      ]}
    >
      <GridLabel title="Country" area="country" overflow>
        <Select
          name="country"
          options={countries}
          rules={{required: true}}
        />
      </GridLabel>
      <GridLabel title="State/Province" area="state" overflow>
        <Select
          name="state"
          options={states}
          disabled={!country}
        />
      </GridLabel>
      <GridLabel title="City" area="city" overflow>
        <Textbox name="city" placeholder="e.g. New York" ref={register({required: true})} />
      </GridLabel>
      <GridLabel title="Zip/Postal code" area="postalCode" overflow>
        <Textbox name="postalCode" placeholder="e.g. 99999" ref={register({required: true})} />
      </GridLabel>
      <GridLabel title="Street address" area="address" overflow>
        <Textbox name="address" placeholder="e.g. 314 Fake St." ref={register({required: true})} />
      </GridLabel>
    </GridFieldset>
  );
};

Step2.fields = [
  'country',
  'state',
  'city',
  'postalCode',
  'address',
];

const RadioGridLabel = styled(GridLabel)`
  margin-left: 36px;
`;

const Step3 = () => {
  const {register, watch} = useFormContext();
  const personalInfo = watch('personalInfo');
  const dpo = watch('dpo');

  return (
    <GridFieldset
      title="A few final questions."
      subtitle="Step 3 of 3"
      description={(
        <>
          Let&rsquo;s customize your privacy policy with some optional clauses unique to your business.
        </>
      )}
      columns="1fr"
      layout={[
        'personalInfo',
        'dpo',
        'customClauses',
      ]}
    >
      <GridField area="personalInfo">
        <GridLabel
          title="Do you allow users to send a request to view, edit, or delete their personal info stored on your site?"
          forwardedAs="div"
        >
          <Radio name="personalInfo" label="No" value="0" ref={register({required: true})} />
          <Radio name="personalInfo" label="Yes" value="1" ref={register({required: true})} />
        </GridLabel>
        {personalInfo === '1' && (
          <>
            <RadioGridLabel
              title="How should customers request to view, edit, or delete their personal info?"
            >
              <Textbox name="deletePersonalInfo" ref={register({required: true})} />
            </RadioGridLabel>
          </>
        )}
      </GridField>
      <GridField area="dpo">
        <GridLabel
          title="Would you like to appoint a Data Protection Officer (DPO) for your company?"
          description="A DPO is required by EU privacy laws, so if you are based in or have customers in the EU, we recommend having a DPO (you can, of course, be your own DPO)."
          descriptionAtTop
          forwardedAs="div"
        >
          <Radio name="dpo" label="No" value="0" ref={register({required: true})} />
          <Radio name="dpo" label="Yes" value="1" ref={register({required: true})} />
        </GridLabel>
        {dpo === '1' && (
          <>
            <RadioGridLabel
              title="What is your DPO’s full name?"
            >
              <Textbox name="dpoFullName" ref={register({required: true})} />
            </RadioGridLabel>
            <RadioGridLabel
              title="What is your DPO’s email address?"
            >
              <Textbox name="dpoEmailAddress" ref={register({required: true})} />
            </RadioGridLabel>
            <RadioGridLabel
              title="What is your DPO’s phone number?"
            >
              <Textbox name="dpoPhoneNumber" ref={register({required: true})} />
            </RadioGridLabel>
          </>
        )}
      </GridField>
      <GridLabel
        title="Do you want to add any custom clauses to your privacy policy?"
        area="customClauses"
      >
        <Textarea
          name="customClauses"
          ref={register}
          rows={3}
        />
      </GridLabel>
    </GridFieldset>
  );
};

Step3.fields = [
  'personalInfo',
  'dpo',
  'customClauses',
];

const Steps = [
  Step1,
  Step2,
  Step3,
];

const PrivacyPolicy = () => {
  const [submitting, setSubmitting] = useState(false);
  const onSubmit = useCallback(async (values) => {
    setSubmitting(true);

    try {
      await fetch('/api/privacy-policy', {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });

      if (window.parentIFrame) {
        window.parentIFrame.sendMessage({complete: true});
      }
    } catch (err) {
      setSubmitting(false);
    }
  }, []);

  return (
    <StepForm
      steps={Steps}
      submitLabel="Generate Privacy Policy"
      loadingLabel="Generating, please wait…"
      onSubmit={onSubmit}
      submitting={submitting}
      defaultValues={{
        paymentProcessor: [],
        dpo: '0',
        personalInfo: '0',
      }}
    />
  );
};

export default PrivacyPolicy;
